import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import { fetchJobs, fetchTemplates, toggleJobActive, deleteJob, triggerSendTest, assertTestDestinationConfigured, isConfirmationRequiredError } from "./services/exportJobsService";
import { ExportJobMappingModal } from "./ExportJobMappingModal";
import { Toast, useToast } from "../../components/Toast";

// Same-destination confirmation (test destination matches production) is
// now decided server-side, transport-specifically, by the
// trigger_send_test() RPC (059) — comparing full JSON.stringify'd objects
// client-side missed cases like same host/URL with different credentials.
// This component just reacts to the RPC's CONFIRMATION_REQUIRED signal.

// A never-run job (exported_through IS NULL) can no longer be activated on
// its own — migration 082's trigger rejects it, because a NULL watermark
// makes the worker cold-start from the 1st of the current UTC month (the
// 2026-09-23 incident: 956 month-to-date conversions sent on first
// activation). Default the prompt to yesterday, expressed as a UTC date at
// 00:00, so a fresh activation only ever sends from "yesterday onward"
// unless the operator picks an earlier date on purpose.
function defaultActivationDateUTC() {
  const now = new Date();
  const yesterday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 1));
  return yesterday.toISOString().slice(0, 10);
}

// A future start date makes the worker skip every conversion before it, and
// migration 078's forward-only guard means exported_through can never be
// moved back afterward to correct that — so both the picker (max) and the
// confirm handler (validation) must reject anything after today (UTC).
// Today itself is allowed.
function todayUTC() {
  return new Date().toISOString().slice(0, 10);
}

export function ExportJobsPanel({ onViewHistory }) {
  // export_jobs became support-owned (DEV-2116): advertisers keep read access
  // (job config + execution history) but can no longer create, edit,
  // delete, pause/resume, or send tests — bad field mappings break network
  // billing, so internal support owns transfer setup. Gate every write
  // affordance on isSupportUser, not canManageAdvertiserData.
  const { effectiveAdvertiserId: advertiserId, isSupportUser } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalJob, setModalJob] = useState(undefined); // undefined = closed, null = new, object = edit
  const [confirmSendTestJobId, setConfirmSendTestJobId] = useState(null);
  const [confirmDeleteJobId, setConfirmDeleteJobId] = useState(null);
  const [sendingTestId, setSendingTestId] = useState(null);
  const [activateJobId, setActivateJobId] = useState(null);
  const [activateDate, setActivateDate] = useState("");
  const [toast, showToast] = useToast(5000);

  const load = async () => {
    if (!supabase || advertiserId == null) { setLoading(false); return; }
    setLoading(true);
    const [{ data: jobsData, error: jobsErr }, { data: templatesData }] = await Promise.all([
      fetchJobs(advertiserId),
      fetchTemplates(),
    ]);
    if (jobsErr) showToast(`Failed to load export jobs: ${jobsErr.message}`, "error");
    setJobs(jobsData || []);
    setTemplates(templatesData || []);
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [advertiserId]);

  const handleToggleActive = async (job, exportedThrough) => {
    const nextActive = !job.is_active;
    const { error } = await toggleJobActive(job.id, nextActive, exportedThrough);
    if (error) { showToast(`Failed to update job: ${error.message}`, "error"); return; }
    setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, is_active: nextActive, ...(exportedThrough !== undefined ? { exported_through: exportedThrough } : {}) } : j)));
    showToast("Job updated.");
  };

  const handleToggleClick = (job) => {
    // Resuming (activating) a job that has never run (exported_through
    // IS NULL) needs an explicit start date first — 082's DB trigger
    // rejects an activation that would leave it NULL. Pausing, and
    // resuming a job that already has a watermark, are unchanged.
    if (!job.is_active && job.exported_through == null) {
      setActivateDate(defaultActivationDateUTC());
      setActivateJobId(job.id);
      return;
    }
    handleToggleActive(job);
  };

  const handleConfirmActivate = (job) => {
    if (activateDate > todayUTC()) {
      showToast("Choose today or an earlier date.", "error");
      return;
    }
    const exportedThrough = `${activateDate}T00:00:00.000Z`;
    setActivateJobId(null);
    handleToggleActive(job, exportedThrough);
  };

  const handleDelete = async (job) => {
    const { error } = await deleteJob(job.id);
    if (error) showToast(`Failed to delete job: ${error.message}`, "error");
    else { setJobs((prev) => prev.filter((j) => j.id !== job.id)); showToast("Export job removed."); }
    setConfirmDeleteJobId(null);
  };

  const runSendTest = async (job, confirmed) => {
    setSendingTestId(job.id);
    const { error } = await triggerSendTest(job, { confirmed });
    setSendingTestId(null);
    if (error && isConfirmationRequiredError(error)) {
      setConfirmSendTestJobId(job.id);
      return;
    }
    setConfirmSendTestJobId(null);
    if (error) showToast(error.message, "error");
    else showToast("Test run queued — check execution history shortly.");
  };

  const handleSendTestClick = (job) => {
    const guard = assertTestDestinationConfigured(job);
    if (!guard.ok) { showToast(guard.message, "error"); return; }
    runSendTest(job, false);
  };

  if (loading) return <div style={{ padding: 24, color: "#7b7b7b" }}>Loading export jobs…</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
        <div>
          <h2 style={{ fontSize: 28, fontWeight: 600, color: "#151515", marginBottom: 4, fontFamily: "'Clash Display Variable','Clash Display',sans-serif" }}>Affiliate Conversion Exports</h2>
          <p style={{ color: "#626262", fontSize: 13 }}>Send billable conversions to your affiliate network on a daily schedule.</p>
        </div>
        {isSupportUser && (
          <button
            onClick={() => setModalJob(null)}
            style={{ padding: "9px 16px", background: "#F90091", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}
          >
            + New Export Job
          </button>
        )}
      </div>

      {jobs.length === 0 && <div style={{ color: "#7b7b7b", fontSize: 13 }}>No export jobs configured yet.</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {jobs.map((job) => {
          const transportType = job.export_templates?.transport_type;
          const guard = assertTestDestinationConfigured(job);
          const sending = sendingTestId === job.id;
          return (
            <div key={job.id} style={{ background: "#FFFFFF", border: "1px solid #E2E2E2", borderRadius: 14, padding: 18, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontWeight: 600, color: "#151515", fontSize: 14 }}>{job.export_templates?.platform_name}</span>
                    <span style={{ fontSize: 12, padding: "2px 7px", borderRadius: 20, background: job.is_active ? "#d8fffa" : "#F5F5F5", color: job.is_active ? "#208a3e" : "#B8B8B8", fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>
                      {job.is_active ? "Active" : "Paused"}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: "#626262", marginTop: 4 }}>{transportType}</div>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button onClick={() => onViewHistory?.(job)} style={{ padding: "6px 12px", background: "#FAFAFA", border: "1px solid #E2E2E2", borderRadius: 7, color: "#626262", fontSize: 14, cursor: "pointer" }}>History</button>
                  {isSupportUser && (
                    <>
                      <button onClick={() => setModalJob(job)} style={{ padding: "6px 12px", background: "transparent", border: "1px solid #E2E2E2", borderRadius: 7, color: "#626262", fontSize: 14, cursor: "pointer" }}>Edit</button>
                      <button onClick={() => handleToggleClick(job)} style={{ padding: "6px 12px", background: "transparent", border: "1px solid #E2E2E2", borderRadius: 7, color: "#626262", fontSize: 14, cursor: "pointer" }}>{job.is_active ? "Pause" : "Resume"}</button>
                      <button onClick={() => setConfirmDeleteJobId(job.id)} style={{ padding: "6px 12px", background: "transparent", border: "1px solid #fca5a5", borderRadius: 7, color: "#e53935", fontSize: 14, cursor: "pointer" }}>Delete</button>
                      <button
                        onClick={() => handleSendTestClick(job)}
                        disabled={!guard.ok || sending}
                        title={!guard.ok ? guard.message : undefined}
                        style={{
                          padding: "6px 12px", borderRadius: 7, fontSize: 14, fontWeight: 600,
                          border: `1px solid ${guard.ok ? "#F90091" : "#E2E2E2"}`,
                          background: guard.ok ? "#FFE9F6" : "#F5F5F5",
                          color: guard.ok ? "#F90091" : "#B8B8B8",
                          cursor: guard.ok && !sending ? "pointer" : "not-allowed",
                        }}
                      >
                        {sending ? "Sending…" : "Send Test"}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {confirmDeleteJobId === job.id && (
                <div style={{ marginTop: 12, padding: "10px 14px", background: "#FFF5F5", border: "1px solid #fca5a5", borderRadius: 10 }}>
                  <div style={{ fontSize: 12, color: "#151515", marginBottom: 8 }}>
                    Delete this export job? Its field mappings and stored credentials are removed and this can't be undone.
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => handleDelete(job)} style={{ padding: "6px 14px", background: "#e53935", color: "#fff", border: "none", borderRadius: 7, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Yes, delete</button>
                    <button onClick={() => setConfirmDeleteJobId(null)} style={{ padding: "6px 14px", background: "transparent", border: "1px solid #E2E2E2", borderRadius: 7, color: "#626262", fontSize: 14, cursor: "pointer" }}>Cancel</button>
                  </div>
                </div>
              )}

              {activateJobId === job.id && (
                <div style={{ marginTop: 12, padding: "10px 14px", background: "#FFF5FB", border: "1px solid #F9009133", borderRadius: 10 }}>
                  <div style={{ fontSize: 12, color: "#151515", marginBottom: 8 }}>
                    This job has never run. Pick a date to resume from, conversions judged billable since then will be sent on the next daily run.
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <label htmlFor={`activate-date-${job.id}`} style={{ fontSize: 12, color: "#626262" }}>Export conversions from</label>
                    <input
                      id={`activate-date-${job.id}`}
                      type="date"
                      value={activateDate}
                      max={todayUTC()}
                      onChange={(e) => setActivateDate(e.target.value)}
                      style={{ padding: "5px 8px", border: "1px solid #E2E2E2", borderRadius: 6, fontSize: 13 }}
                    />
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => handleConfirmActivate(job)} disabled={!activateDate} style={{ padding: "6px 14px", background: "#F90091", color: "#fff", border: "none", borderRadius: 7, fontSize: 14, fontWeight: 600, cursor: activateDate ? "pointer" : "not-allowed" }}>Activate</button>
                    <button onClick={() => setActivateJobId(null)} style={{ padding: "6px 14px", background: "transparent", border: "1px solid #E2E2E2", borderRadius: 7, color: "#626262", fontSize: 14, cursor: "pointer" }}>Cancel</button>
                  </div>
                </div>
              )}

              {confirmSendTestJobId === job.id && (
                <div style={{ marginTop: 12, padding: "10px 14px", background: "#FFF5FB", border: "1px solid #F9009133", borderRadius: 10 }}>
                  <div style={{ fontSize: 12, color: "#151515", marginBottom: 8 }}>
                    This job's test destination matches production. Confirm you want to send a test payload to it.
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => runSendTest(job, true)} style={{ padding: "6px 14px", background: "#F90091", color: "#fff", border: "none", borderRadius: 7, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Yes, send test</button>
                    <button onClick={() => setConfirmSendTestJobId(null)} style={{ padding: "6px 14px", background: "transparent", border: "1px solid #E2E2E2", borderRadius: 7, color: "#626262", fontSize: 14, cursor: "pointer" }}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {modalJob !== undefined && (
        <ExportJobMappingModal
          advertiserId={advertiserId}
          job={modalJob}
          templates={templates}
          onClose={() => setModalJob(undefined)}
          onSaved={load}
        />
      )}

      <Toast {...toast} />
    </div>
  );
}
